import { useEffect, useState } from 'react';
import { ragApi } from '../api/apiRag';

function QASection() {
    const [questions, setQuestions] = useState([]);
    const [newQuestionText, setNewQuestionText] = useState('');
    const [newAnswerText, setNewAnswerText] = useState('');
    const [loadingAdd, setLoadingAdd] = useState(false);
    const [loadingConfirm, setLoadingConfirm] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showResultDialog, setShowResultDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);
    const [selectedQuestionId, setSelectedQuestionId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                const data = await ragApi.get('/questions');
                if (data) {
                    try {
                        setQuestions(data);
                    } catch (jsonError) {
                        console.error("Failed to parse JSON from /questions:", jsonError);
                        setDialogMessage('Error: The server returned an invalid response.');
                        setShowResultDialog(true);
                    }
                } else {
                    console.error("Failed to fetch questions, status.");
                    setQuestions([]);
                }
            } catch (error) {
                console.error("Error fetching questions:", error);
            }
        };
        fetchQuestions();
    }, []);

    const handleTextChange = (e, id, field) => {
        const updatedQuestions = questions.map((q) => {
            if (q.id === id) {
                return { ...q, [field]: e.target.value };
            }
            return q;
        });
        setQuestions(updatedQuestions);
    };

    const handleSave = async () => {
        setLoadingConfirm(true);
        if (selectedQuestionId) {
            const selectedQuestion = questions.find(q => q.id === selectedQuestionId);
            const { pregunta, respuesta } = selectedQuestion;

            const resApi = await ragApi.post('/vectorQuestionUpdate', { id: selectedQuestionId, question: pregunta, answer: respuesta });

            if (!resApi || resApi.status !== 'success') {
                setDialogMessage('Error updating question in vector store.');
                setShowResultDialog(true);
                setLoadingConfirm(false);
                return;
            }
            setDialogMessage('Content updated successfully!');
        }
        setLoadingConfirm(false);
        setShowConfirmDialog(false);
        setShowResultDialog(true);
    };

    const handleDelete = async () => {
        setLoadingConfirm(true); // Show loader and disable buttons
        if (selectedQuestionId) {
            const resApi = await ragApi.post('/vectorDelete', { id: selectedQuestionId, type: 'pregunta' });

            if (resApi && resApi.status === 'success') {
                setQuestions(questions.filter((q) => q.id !== selectedQuestionId));
                setDialogMessage('Item deleted successfully!');
            } else {
                setDialogMessage('Error deleting vector in vector DB.');
            }
        }
        setLoadingConfirm(false);
        setShowConfirmDialog(false);
        setShowResultDialog(true); 
    };

    const handleAddNewQuestion = async () => {
        if (newQuestionText.trim() === '' || newAnswerText.trim() === '') {
            setDialogMessage('Please enter both a question and an answer.');
            setShowResultDialog(true);
            return;
        }

        setLoadingAdd(true);
        
        const resApi = await ragApi.post('/vectorQuestionSave', { question: newQuestionText, answer: newAnswerText });


        if (resApi && resApi.status === 'success') {
            const updatedQuestions = await ragApi.get('/questions');
            setQuestions(updatedQuestions);
            setNewQuestionText('');
            setNewAnswerText('');
            setDialogMessage('New question added successfully!');
        } else {
            setDialogMessage('Error in vector store insertion.');
        }

        setLoadingAdd(false);
        setShowResultDialog(true);
    };

    const openConfirmDialog = (action, id) => {
        setSelectedQuestionId(id);
        setConfirmAction(action);
        setShowConfirmDialog(true);
    };

    const handleConfirmAction = () => {
        if (confirmAction === 'delete') {
            handleDelete();
        } else if (confirmAction === 'save') {
            handleSave();
        }
    };

    const filteredQuestions = questions.filter((q) => 
        q.pregunta.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-4 pt-0 mt-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <div className="mb-6">
                <textarea
                    value={newQuestionText}
                    onChange={(e) => setNewQuestionText(e.target.value)}
                    placeholder="Add a new question..."
                    className="w-full h-12 p-3 border rounded-lg text-lg resize-none mb-2"
                />
                <textarea
                    value={newAnswerText}
                    onChange={(e) => setNewAnswerText(e.target.value)}
                    placeholder="Add the answer..."
                    className="w-full h-24 p-3 border rounded-lg text-lg resize-vertical mb-2"
                />
                <button 
                    onClick={handleAddNewQuestion}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
                    disabled={loadingAdd}
                >
                    Add New Question
                    {loadingAdd && (
                        <svg
                            className="animate-spin ml-2 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                    )}
                </button>
            </div>

            <div className="my-4">
                <hr className="border-gray-400" />
                <h2 className="text-xl font-semibold text-gray-700 text-center mt-2">Questions in Vectorstore</h2>
            </div>

            <div className="mb-6">
                <input 
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search questions..."
                    className="w-full p-2 border rounded-lg text-lg"
                />
            </div>

            {filteredQuestions.map((q) => (
                <div key={q.id} className="grid grid-cols-12 items-start bg-gray-100 p-2 mb-2 rounded-md border-b border-gray-300">
                    <div className="col-span-10 w-full">
                        <h2 className="text-xl font-semibold text-gray-700 text-left mb-1">Question:</h2>
                        <textarea 
                            value={q.pregunta} 
                            onChange={(e) => handleTextChange(e, q.id, 'pregunta')}
                            className="w-full h-12 p-3 border rounded-lg text-lg resize-none mb-2"
                            placeholder="Question"
                        />
                        <h2 className="text-xl font-semibold text-gray-700 text-left mb-1">Answer:</h2>
                        <textarea 
                            value={q.respuesta} 
                            onChange={(e) => handleTextChange(e, q.id, 'respuesta')}
                            className="w-full h-40 p-3 border rounded-lg text-lg resize-vertical whitespace-pre-wrap"
                            placeholder="Answer"
                        />
                    </div>
                    <div className="col-span-2 flex flex-col space-y-2 justify-center h-full">
                        <button 
                            onClick={() => openConfirmDialog('delete', q.id)}
                            className="mx-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                            Delete
                        </button>
                        <button 
                            onClick={() => openConfirmDialog('save', q.id)}
                            className="mx-4 py-2 bg-[#4790CF] text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Save
                        </button>
                    </div>
                </div>
            ))}
            
            {showConfirmDialog && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50">
                    <div className="bg-white p-6 rounded-md shadow-lg">
                        <p>Are you sure you want to {confirmAction === 'delete' ? 'delete' : 'save'} this item?</p>
                        <div className="mt-4 flex justify-end">
                            <button 
                                className="mr-2 px-4 py-2 bg-gray-300 rounded-md" 
                                onClick={() => setShowConfirmDialog(false)}
                                disabled={loadingConfirm}
                            >
                                Cancel
                            </button>
                            <button 
                                className={`px-4 py-2 text-white rounded-md transition-colors ${
                                    confirmAction === 'delete' ? 'bg-red-500 hover:bg-red-700' : 'bg-[#4790CF] hover:bg-blue-700'
                                } flex items-center`}
                                onClick={handleConfirmAction}
                                disabled={loadingConfirm}
                            >
                                {confirmAction === 'delete' ? 'Delete' : 'Save'}
                                {loadingConfirm && (
                                    <svg className="animate-spin ml-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showResultDialog && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50">
                    <div className="bg-white p-6 rounded-md shadow-lg">
                        <p>{dialogMessage}</p>
                        <div className="mt-4 flex justify-end">
                            <button 
                                className="px-4 py-2 bg-[#4790CF] text-white rounded-md" 
                                onClick={() => setShowResultDialog(false)}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default QASection;
